import { useEffect, useState } from 'react';
import { Button, Table, Space, message, Popconfirm, Tag, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import lotteryApi from 'api/lottery';
import ModalPrize from './components/ModalPrize';
import UserList from './components/UserList';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import SortableRow from './components/SortableRow';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const PrizeManagement = () => {
    const [prizes, setPrizes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPrize, setEditingPrize] = useState(null);

    const { t } = useTranslation();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchPrizes();
    }, []);

    const fetchPrizes = async () => {
        try {
            setLoading(true);
            const response = await lotteryApi.getAll();
            const sortedPrizes = (response.lotteries || []).sort((a, b) => a.order - b.order);
            setPrizes(sortedPrizes);
        } catch (error) {
            console.log("🚀 ~ error:", error)
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = prizes.findIndex((item) => item._id === active.id);
            const newIndex = prizes.findIndex((item) => item._id === over.id);

            const newPrizes = arrayMove(prizes, oldIndex, newIndex);

            // Update local state immediately for better UX
            setPrizes(newPrizes);

            // Update order in backend
            try {
                const updatePromises = newPrizes.map((prize, index) =>
                    lotteryApi.update(prize._id, { ...prize, order: index })
                );
                await Promise.all(updatePromises);
                message.success(t('updatedIndexSuccessfully'));
                fetchPrizes()
            } catch (error) {
                message.error(t('hasError'));
                fetchPrizes();
            }
        }
    };

    const handleCreate = () => {
        setEditingPrize(null);
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingPrize(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await lotteryApi.delete(id);
            message.success(t('deletedPrizeSuccessfully'));
            fetchPrizes();
        } catch (error) {
            message.error(t('hasError'));
            console.error('Error deleting prize:', error);
        }
    };

    const handleModalSubmit = async (values) => {
        try {
            if (editingPrize) {
                await lotteryApi.update(editingPrize._id, values);
                message.success(t('updatedPrizeSuccessfully'));
            } else {
                // Set order to be the last
                const maxOrder = prizes.length > 0 ? Math.max(...prizes.map(p => p.order)) : -1;
                await lotteryApi.create({ ...values, order: maxOrder + 1 });
                message.success(t('createPrizeSuccessfully'));
            }
            setModalVisible(false);
            fetchPrizes();
        } catch (error) {
            message.error(t('hasError'));
        }
    };

    const columns = [
        {
            title: '',
            key: 'sort',
            render: () => null, // The drag handle is rendered by SortableRow
        },
        {
            title: t('index'),
            dataIndex: 'order',
            key: 'order',
            render: (text) => text + 1,
        },
        {
            title: t('prizeName'),
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: t('quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
        },
        {
            title: t('onlyMarker'),
            dataIndex: 'onlyMarker',
            key: 'onlyMarker',
            render: (onlyMarker) => (
                <Tag color={onlyMarker ? 'green' : 'default'}>
                    {onlyMarker ? t('yes') : t('no')}
                </Tag>
            ),
        },
        {
            title: t('spinCount'),
            dataIndex: 'spinCount',
            key: 'spinCount',
        },
        {
            title: t('action'),
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        {t('edit')}
                    </Button>
                    <Popconfirm
                        title={t('sureToDelete')}
                        onConfirm={() => handleDelete(record._id)}
                        okText={t('delete')}
                        cancelText={t('cancel')}
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            {t('delete')}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const tabItems = [
        {
            key: '1',
            label: t('prizeManagement'),
            children: (
                <>
                    <div className={styles.header}>
                        <h2>{t('prizeManagement')}</h2>
                        <Space>
                            <Link to="/lottery">
                                <Button>{t('luckyDraw')}</Button>
                            </Link>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                            >
                                {t('newPrize')}
                            </Button>
                        </Space>
                    </div>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis]}
                    >
                        <SortableContext
                            items={prizes.map(p => p._id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <Table
                                dataSource={prizes}
                                columns={columns}
                                rowKey="_id"
                                loading={loading}
                                pagination={false}
                                components={{
                                    body: {
                                        row: SortableRow,
                                    },
                                }}
                            />
                        </SortableContext>
                    </DndContext>

                    <ModalPrize
                        visible={modalVisible}
                        onCancel={() => setModalVisible(false)}
                        onSubmit={handleModalSubmit}
                        initialValues={editingPrize}
                    />
                </>
            ),
        },
        {
            key: '2',
            label: t('listEmployee'),
            children: <UserList />,
        },
    ];

    return (
        <div className={styles.prizeManagement}>
            <Tabs defaultActiveKey="1" items={tabItems} />
        </div>
    );
};

export default PrizeManagement;
